import React, { useState, useEffect } from 'react'
import { Modal, Input, Button, Form } from 'semantic-ui-react'
import PropTypes from "prop-types"
import { Currencies } from '../shared/currencies'
import { useTextResources, useMessageResources } from '../../language'
import { onWarning, onError, onSuccess } from '../shared/messages'
import { CreditCardAPIPayment } from '../../api/services/finance'
import { useLoader } from '../../hooks/useLoader'
import { Folio } from '../../api/services/next'
import { ECompanyPayment } from '../Payment/paymentCommon'


const RefundPopup = ({ controller }) => {
    const [open, setOpen] = useState(false)
    const [params, setParams] = useState()
    const [refundAmount, setRefundAmount] = useState(0)
    const [maxPossibleRefund, setMaxPossibleRefund] = useState(0)
    const [data, setData] = useState({})
    const text = useTextResources()
    const message = useMessageResources()
    const loader = useLoader()

    useEffect(() => {
        if (controller) {
            window[controller] = async (action, params) => {
                switch (action) {
                    case "open":
                        if (params) {
                            setParams(params)
                            // If folioTransactionId is provided, fetch data from API
                            if (params.FolioTransactionId) {
                                const shouldOpen = await loadTransactionRefundDetails(params.FolioTransactionId)
                                if (shouldOpen) {
                                    setOpen(true)
                                }
                            } else {
                                setOpen(true)
                            }
                        } else {
                            setOpen(true)
                        }
                        break;
                    default:
                        break;
                }
            }
        }
    }, [])

    const loadTransactionRefundDetails = async (folioTransactionId) => {
        try {
            loader(true)

            const refundDetails = await Folio.GetTransactionRefundsDetails({
                OriginalTransactionIds: [folioTransactionId]
            })

            // Check if we have valid refund details
            if (!refundDetails || refundDetails.length === 0) {
                onError('Failed to load transaction refund details')
                return false
            }

            const transaction = refundDetails[0]

            // Only allow refunds for Credit Guard payments
            if (transaction.CompanyPaymentId !== ECompanyPayment.CREDITGUARD) {
                onWarning('Refunds can only be made to Credit Guard. For refunds, please contact your credit card company')
                return false
            }

            setData(transaction)
            const maxRefund = Math.min(transaction.TotalRefundAmount || 0, transaction.Amount || 0)
            setRefundAmount(maxRefund)
            setMaxPossibleRefund(maxRefund)
            
            return true // Open the modal
        } catch (error) {
            onError(error?.message || 'Failed to load transaction refund details')
            return false
        } finally {
            loader(false)
        }
    }

    const handleRefundAmountChange = (e) => {
        const value = e.target.value

        if (validateRefundAmount(value)) {
            setRefundAmount(value)
        }
    }

    const currencySymbol = Currencies[data?.CurrencyId || 0]?.symbol || ''

    const validateRefundAmount = (amount) => {

        if (amount < 0) {
            onWarning(message("RefundAmountMustBeGreaterThan"))
            return false
        }

        if (amount > maxPossibleRefund) {
            const maxAmountFormatted = `${currencySymbol}${maxPossibleRefund.toFixed(2)}`;
            onWarning(message("RefundAmountCannotExceed").replace('{0}', maxAmountFormatted));
            return false
        }

        return true
    }

    const handleSave = async () => {
        try {
            loader(true)

            var result = await CreditCardAPIPayment({
                TranId: data?.TranId,
                IsExternalPayment: false,
                HotelId: data?.HotelId,
                TotalCharge: refundAmount * -1,
                IsRefundDeal: true,
                CompanyPayment: data?.CompanyPaymentId,
                OriginalFolioTransactionID: data?.TransactionId,
                CardExpDate: data?.CardExpDate,
                Language: 3,
                Currency: data?.CurrencyId,
                FolioId: data?.FolioId,
                EntityTypeId: data?.EntityTypeId,
                EntityId: data?.EntityId,
                Token: data?.Token,
                ExtEntity: [{ EntityTypeId: data?.FolioEntityTypeId, EntityId: data?.FolioEntityId }],
                PaymentType: 1,
                PaymentProviderTransactionId: data?.PaymentProviderTransactionId,
                Terminal: data?.TerminalId,
            })
            if (result.ErrorCode) {
                onWarning(result.ErrorText)
            }
            else {
                params?.handleSave?.()
                handleClose()
                onSuccess()
            }

        } catch (error) {
            onError(error?.message)
        }
        finally {
            loader(false)
        }
    }
    const handleClose = () => {
        setRefundAmount(0)
        setData({})
        setParams(null)
        setOpen(false)
        params?.onClose?.()
    }

    return (
        <Modal open={open} size="small">
            <Modal.Header>
                {text("Refund")}
            </Modal.Header>
            <Modal.Content>
                <Form>
                    {data && <Form.Field>
                        <label>
                            {text("RefundAmount")}
                            <span style={{ color: '#999', fontSize: '0.9em', marginLeft: '10px' }}>
                                {text("Maximum")}:
                                {maxPossibleRefund?.toFixed(2)}
                                {currencySymbol}
                            </span>
                        </label>
                        <Input
                            type="number"
                            placeholder="0.00"
                            value={refundAmount || 0}
                            onChange={handleRefundAmountChange}
                            label={{ basic: true, content: currencySymbol }}
                            labelPosition="left"
                            min="0"
                            max={maxPossibleRefund}
                        />
                    </Form.Field>}
                </Form>

            </Modal.Content>
            <Modal.Actions>
                <Button onClick={handleClose}>
                    {text("Cancel")}
                </Button>
                <Button 
                    positive 
                    onClick={handleSave} 
                    disabled={data?.CompanyPaymentId !== ECompanyPayment.CREDITGUARD}>
                    {text("Save")}
                </Button>
            </Modal.Actions>
        </Modal>
    )
}

RefundPopup.propTypes = {
    controller: PropTypes.any,
}

export default RefundPopup
